/**
 * Layout state for the Orientation Editor's floating workspace. The map fills
 * the canvas; Stops / Inspector float over it as draggable, resizable cards
 * (pointer-capture drags, so the map / 3D iframe underneath never steals the
 * gesture); the timeline is a bottom drawer resized from its top edge. First
 * run places the cards from the measured canvas; afterwards the layout
 * persists per browser and re-clamps whenever the canvas shrinks.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react';
import {
  clampDrawerH, clampLayout, clampRect, clampW, clearLayout, defaultLayout, loadLayout, saveLayout,
  type CanvasSize, type EditorLayout, type FloatKey, type Rect,
} from './layout';
import type { DrawerShell, FloatShell, ResizeEdge } from './PanelShell';

export function useEditorLayout() {
  const [canvas, setCanvas] = useState<CanvasSize | null>(null);
  const [layout, setLayout] = useState<EditorLayout | null>(loadLayout);
  const [front, setFront] = useState<FloatKey>('inspector');

  // Measure the canvas, and re-clamp cards when it shrinks (window resize) so
  // nothing ends up stranded off-screen. A CALLBACK ref, not a mount effect:
  // the canvas div is conditional (empty states render without it), so the
  // observer must attach whenever the node actually appears, not just on the
  // hook's first mount.
  const roRef = useRef<ResizeObserver | null>(null);
  const canvasRef = useCallback((el: HTMLDivElement | null) => {
    roRef.current?.disconnect();
    roRef.current = null;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r && r.width > 0 && r.height > 0) setCanvas({ w: r.width, h: r.height });
    });
    ro.observe(el);
    roRef.current = ro;
    // Measure synchronously too — the initial RO callback is async, and the
    // first-run default layout should not wait a frame.
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) setCanvas({ w: r.width, h: r.height });
  }, []);
  useEffect(() => () => roRef.current?.disconnect(), []);

  // First run (nothing persisted): derive the default placement from the
  // measured canvas. Re-runs are no-ops because layout is set after.
  useEffect(() => {
    if (layout == null && canvas != null) setLayout(defaultLayout(canvas));
  }, [layout, canvas]);

  useEffect(() => {
    if (layout != null && canvas != null) setLayout((l) => (l ? clampLayout(l, canvas) : l));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas]);

  // Debounced persistence: pointer drags update state once per pointermove.
  useEffect(() => {
    if (layout == null) return;
    const t = setTimeout(() => saveLayout(layout), 300);
    return () => clearTimeout(t);
  }, [layout]);

  const patchPanel = (k: FloatKey, patch: Partial<{ rect: Rect; collapsed: boolean; docked: boolean }>) =>
    setLayout((l) => (l ? { ...l, panels: { ...l.panels, [k]: { ...l.panels[k], ...patch } } } : l));

  const expandPanel = (k: FloatKey) => patchPanel(k, { collapsed: false });
  const expandTimeline = () =>
    setLayout((l) => (l ? { ...l, timeline: { ...l.timeline, collapsed: false } } : l));

  const resetLayout = () => {
    clearLayout();
    // No canvas mounted (empty state): null the state too, or the debounced
    // save would just re-persist the old layout; defaults re-derive on mount.
    setLayout(canvas ? defaultLayout(canvas) : null);
  };

  /** Shared pointer-drag runner: capture the pointer on the handle so the
   *  map/iframe below never sees the gesture, then stream deltas. */
  function dragWith(e: ReactPointerEvent, apply: (dx: number, dy: number) => void) {
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget as HTMLElement;
    const startX = e.clientX;
    const startY = e.clientY;
    const onMove = (ev: PointerEvent) => apply(ev.clientX - startX, ev.clientY - startY);
    const onUp = () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      try { el.releasePointerCapture(e.pointerId); } catch { /* already released */ }
    };
    try { el.setPointerCapture(e.pointerId); } catch { /* unsupported */ }
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
  }

  function startMove(e: ReactPointerEvent, k: FloatKey) {
    // Header buttons/inputs keep their own clicks.
    if ((e.target as HTMLElement).closest('button, select, input, textarea')) return;
    if (layout == null || canvas == null || layout.panels[k].docked) return;
    const start = layout.panels[k].rect;
    setFront(k);
    dragWith(e, (dx, dy) =>
      patchPanel(k, { rect: clampRect(k, { ...start, x: start.x + dx, y: start.y + dy }, canvas) }));
  }

  function startResize(e: ReactPointerEvent, k: FloatKey, edge: ResizeEdge) {
    if (layout == null || canvas == null) return;
    const start = layout.panels[k].rect;
    setFront(k);
    if (layout.panels[k].docked) {
      // A docked column only resizes from its inner edge; width is all that moves.
      dragWith(e, (dx) =>
        patchPanel(k, { rect: { ...start, w: clampW(k, k === 'stops' ? start.w + dx : start.w - dx) } }));
      return;
    }
    dragWith(e, (dx, dy) => {
      let { x, w, h } = start;
      if (edge === 'e' || edge === 'se') w = start.w + dx;
      if (edge === 'w') {
        // Clamp the width FIRST: deriving x from an unclamped width would keep
        // x tracking the cursor after the limit, sliding the whole card.
        w = clampW(k, start.w - dx);
        x = start.x + (start.w - w);
      }
      if (edge === 's' || edge === 'se') h = start.h + dy;
      patchPanel(k, { rect: clampRect(k, { x, y: start.y, w, h }, canvas) });
    });
  }

  function startDrawerResize(e: ReactPointerEvent) {
    if (layout == null) return;
    const startH = layout.timeline.h;
    const canvasH = canvas?.h;
    dragWith(e, (_dx, dy) =>
      setLayout((l) => (l ? { ...l, timeline: { ...l.timeline, h: clampDrawerH(startH - dy, canvasH) } } : l)));
  }

  const floatShell = (k: FloatKey): FloatShell | null =>
    layout == null
      ? null
      : {
          rect: layout.panels[k].rect,
          collapsed: layout.panels[k].collapsed,
          docked: layout.panels[k].docked,
          side: k === 'stops' ? 'left' : 'right',
          zIndex: front === k ? 40 : k === 'inspector' ? 31 : 30,
          onMoveStart: (e) => startMove(e, k),
          onResizeStart: (e, edge) => startResize(e, k, edge),
          onToggleCollapse: () => patchPanel(k, { collapsed: !layout.panels[k].collapsed }),
          onToggleDock: () => patchPanel(k, { docked: !layout.panels[k].docked }),
          onFocus: () => setFront(k),
        };

  /** Widths the docked columns take from the map and the drawer, as CSS vars
   *  on the canvas (a collapsed docked card is just a title bar: no push). */
  const dockWidth = (k: FloatKey) =>
    layout?.panels[k].docked && !layout.panels[k].collapsed ? layout.panels[k].rect.w : 0;
  const dockB = layout?.timeline.docked && !layout.timeline.collapsed ? layout.timeline.h : 0;
  const dockStyle = {
    '--dock-l': `${dockWidth('stops')}px`,
    '--dock-r': `${dockWidth('inspector')}px`,
    '--dock-b': `${dockB}px`,
  } as CSSProperties;

  const drawerShell: DrawerShell | null =
    layout == null
      ? null
      : {
          h: layout.timeline.h,
          collapsed: layout.timeline.collapsed,
          onResizeStart: startDrawerResize,
          docked: layout.timeline.docked,
          onToggleCollapse: () =>
            setLayout((l) => (l ? { ...l, timeline: { ...l.timeline, collapsed: !l.timeline.collapsed } } : l)),
          onToggleDock: () =>
            setLayout((l) => (l ? { ...l, timeline: { ...l.timeline, docked: !l.timeline.docked } } : l)),
        };

  return { canvasRef, dockStyle, floatShell, drawerShell, expandPanel, expandTimeline, resetLayout };
}
